import { useEffect } from 'react';
import type { Permission } from '@valiosascrm/shared';
import { useCanAccess } from '@/hooks/useCanAccess';
import { computeTenantSlug } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

// Story 65.22 — a escrita nova de um motor financeiro pode estar desligada numa clínica que já
// tem fato gravado dele. Aí a tela continua mostrando e acertando o que existe (`readable`) e
// só esconde o que cria fato novo (`write`). Sem resposta, vale "desligado".

interface AvailabilitySnapshot {
  enabledBy: Record<string, boolean>;
  readOnlyBy: Record<string, boolean>;
  ensure: (key: string) => Promise<void>;
}
interface AvailabilityStore {
  <T>(selector: (state: AvailabilitySnapshot) => T): T;
  getState: () => AvailabilitySnapshot;
}

export function useFinancialAvailability(store: AvailabilityStore, permission: Permission): { write: boolean; readable: boolean } {
  const allowed = useCanAccess(permission);
  const key = useAuthStore((state) => (state.user ? `${state.user.id}:${computeTenantSlug(state) ?? ''}` : ''));
  const enabled = store((state) => (key ? state.enabledBy[key] === true : false));
  const readOnly = store((state) => (key ? state.readOnlyBy[key] === true : false));
  useEffect(() => {
    if (allowed && key) void store.getState().ensure(key);
  }, [allowed, key, store]);
  return { write: allowed && enabled, readable: allowed && (enabled || readOnly) };
}
